import React, { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Github, ExternalLink } from 'lucide-react';
import { projects } from '../../data/mock';

const ProjectCard = ({ project, index }) => {
  const cardRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 30;
    const rotateY = (centerX - x) / 30;
    cardRef.current.style.transform = `perspective(1000px) rotateX(${-rotateX}deg) rotateY(${rotateY}deg)`;
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (cardRef.current) {
      cardRef.current.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg)';
    }
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: index * 0.12, duration: 0.5 }}
    >
      <div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="glass-card rounded-xl overflow-hidden group cursor-pointer h-full"
        style={{
          borderTop: `2px solid ${project.color}`,
          transition: 'transform 0.15s ease-out, box-shadow 0.3s',
          willChange: 'transform',
        }}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="font-display text-2xl font-bold text-[#F0F4FF]">{project.title}</h3>
              <p className="font-mono text-sm" style={{ color: project.color }}>
                {project.tagline}
              </p>
            </div>
            <span
              className="px-2 py-0.5 rounded text-xs font-mono border flex-shrink-0 ml-3"
              style={{
                color: project.badge === 'COMING SOON' ? '#8892A4' : project.color,
                borderColor: project.badge === 'COMING SOON' ? 'rgba(136,146,164,0.3)' : `${project.color}40`,
                background: project.badge === 'COMING SOON' ? 'rgba(136,146,164,0.08)' : `${project.color}10`,
              }}
            >
              {project.badge}
            </span>
          </div>

          {/* Quote */}
          <p className="text-sm text-[#8892A4] italic mb-4">
            "{project.description}"
          </p>

          {/* Callouts */}
          <div className="flex flex-wrap gap-2 mb-4">
            {project.callouts.map((callout, i) => (
              <span
                key={i}
                className="px-2 py-1 rounded text-xs font-mono"
                style={{
                  color: project.color,
                  background: `${project.color}10`,
                  border: `1px solid ${project.color}20`,
                }}
              >
                {callout}
              </span>
            ))}
          </div>

          {/* Description */}
          <p className="text-sm text-[#C0C8D8] mb-4 leading-relaxed">
            {project.fullDescription}
          </p>

          {/* Impact metric pills */}
          {project.impactMetrics && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {project.impactMetrics.map((metric, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 rounded text-xs font-mono font-medium"
                  style={{
                    color: project.color,
                    border: `1px solid ${project.color}35`,
                    background: 'transparent',
                  }}
                >
                  {metric}
                </span>
              ))}
            </div>
          )}

          {/* Stack */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            {project.stack.map((tech, i) => (
              <span
                key={i}
                className="px-2 py-0.5 rounded-full text-xs font-mono text-[#8892A4] bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)]"
              >
                {tech}
              </span>
            ))}
          </div>

          {/* Links */}
          <div className="flex flex-wrap items-center gap-4">
            {project.link !== '#' && (
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-mono transition-opacity duration-200 hover:opacity-80"
                style={{ color: project.color }}
              >
                <Github size={14} /> View on GitHub <ExternalLink size={12} />
              </a>
            )}
            {project.liveLink && (
              <a
                href={project.liveLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-mono px-3 py-1 rounded-md transition-all duration-200 hover:opacity-90"
                style={{
                  color: '#080C14',
                  background: project.color,
                  fontWeight: 600,
                }}
              >
                <ExternalLink size={13} /> Live Demo
              </a>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ProjectsSection = () => {
  return (
    <section id="projects" className="py-24 relative z-10">
      <div className="container mx-auto px-6 lg:px-16">
        <motion.span
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="section-label block mb-4"
        >
          // PROOF OF WORK
        </motion.span>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-display text-3xl lg:text-4xl font-bold text-[#F0F4FF] mb-12"
        >
          Signature Builds
        </motion.h2>

        <div className="grid md:grid-cols-2 gap-6">
          {projects.map((project, index) => (
            <ProjectCard key={index} project={project} index={index} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <a
            href="https://github.com/nihanthnaidu007"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-[rgba(255,255,255,0.15)] text-[#8892A4] font-mono text-sm hover:border-[#00D4FF] hover:text-[#00D4FF] transition-colors duration-300"
          >
            View All Projects <ExternalLink size={14} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectsSection;
